// Form logic for the Buyer integration screen (CU-LS-M1-050).
// Mirrors the admin contract of POST /lds/buyer/add-campaign-integration-setting
// (lds-api #327 request templates, #331 response rules, #334 timeouts, #337 body secrets / value maps).
// The API stays the authority: these checks only catch mistakes early and enforce the
// secret-name-only rule before anything leaves the browser.

import { findSecretValues, isCredentialName, secretReason } from "./secretDetection";

export { isCredentialName };

export const SECRET_REF_PATTERN = /^LDS_BUYER_SECRET_[A-Z0-9_]{1,64}$/;

export const TIMEOUT_MIN_MS = 100;
export const TIMEOUT_MAX_MS = 60000;
// lds.lead.deadline-ms (30,000) minus lds.lead.response-reserve-ms (500); the API re-checks with its live settings.
export const DEFAULT_BUYER_WINDOW_MS = 29500;
// lds-api SETUP_HEADROOM_MS: minTimeoutMs must leave this much of timeout for request building and DNS
export const SETUP_HEADROOM_MS = 1000;

// LDS fields lds-api refuses to map (BuyerRequestTemplates.NOT_MAPPABLE; testMode is a virtual field and allowed)
export const NOT_MAPPABLE_FIELDS = [
  "apiId",
  "apiPassword",
  "test",
  "active",
  "modifiedOn",
  "emailNorm",
  "ssnNorm",
  "phoneNorm",
  "leadUniqueId",
  "createdOn",
  "clickid",
  "subId1",
  "subId2",
  "subId3",
  "subId4",
  "consentText",
];

const TEMPLATE_KEYS = ["format", "contentType", "xmlRoot", "fields", "auth"];
const FIELD_KEYS = [
  "buyerField",
  "valueType",
  "testOnly",
  "ldsField",
  "value",
  "secretRef",
  "envRef",
  "transforms",
  "valueMap",
  "unmappedValue",
  "defaultValue",
  "omitIfEmpty",
  "required",
];
const AUTH_KEYS = ["type", "headerName", "username", "secretRef"];
const RULES_KEYS = [
  "format",
  "statusPath",
  "numericStatus",
  "outcomes",
  "pricePath",
  "bestBidPath",
  "redirectUrlPath",
  "reasonPath",
];
const OUTCOME_KEYS = ["value", "outcome"];
const TIMEOUT_KEYS = ["timeout", "minTimeoutMs"];
const OUTCOMES = ["sold", "rejected", "in_progress", "error", "not_found"];
const LDS_ONLY_OPTIONS = ["valueMap", "unmappedValue", "defaultValue", "required"];

export const SECRET_RULE_HINT =
  "Secrets are never typed here: set the value as an env var named LDS_BUYER_SECRET_* on the API and put only that name in secretRef.";

export const SECRET_HOST_HINT =
  "Ops also sets LDS_BUYER_HOST_<NAME> next to each LDS_BUYER_SECRET_<NAME>: the buyer host names (comma-separated, exact match, e.g. api.acme.com,sandbox.acme.com) the secret may go to. The posting URL must be https:// and its host in that list, or the API refuses the save.";

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const isBlank = (v) => typeof v !== "string" || v.trim() === "";
const unknownKeys = (obj, allowed) =>
  Object.keys(obj).filter((k) => !allowed.includes(k));

// JSON.parse with a readable message ("line 3, column 7: ...").
export function parseJsonText(text) {
  if (text === undefined || text === null || String(text).trim() === "") {
    return { value: null, error: null, empty: true };
  }
  try {
    return { value: JSON.parse(text), error: null, empty: false };
  } catch (e) {
    const match = /position (\d+)/.exec(e.message || "");
    if (match) {
      const pos = Number(match[1]);
      const before = String(text).slice(0, pos).split("\n");
      const line = before.length;
      const column = before[before.length - 1].length + 1;
      return {
        value: null,
        error: `Invalid JSON at line ${line}, column ${column}`,
        empty: false,
      };
    }
    return { value: null, error: "Invalid JSON", empty: false };
  }
}

export const toEditorText = (value) =>
  value === undefined || value === null ? "" : JSON.stringify(value, null, 2);

function checkSecretRef(ref, where, errors) {
  if (typeof ref !== "string" || !SECRET_REF_PATTERN.test(ref)) {
    errors.push(
      `${where}.secretRef must be an env var name like LDS_BUYER_SECRET_ACME_API_KEY (A-Z, 0-9, _), never the secret itself`
    );
  }
}

export function validateRequestTemplate(template) {
  const errors = [];
  if (template === null) return errors; // null = legacy form post
  if (!isPlainObject(template)) return ["requestTemplate must be a JSON object"];
  unknownKeys(template, TEMPLATE_KEYS).forEach((k) =>
    errors.push(`requestTemplate has an unknown key: ${k}`)
  );
  if (
    template.format !== undefined &&
    !["form", "json", "xml"].includes(String(template.format).toLowerCase())
  ) {
    errors.push("format must be form, json or xml");
  }
  if (typeof template.contentType === "string" && /;\s*charset\s*=/i.test(template.contentType)) {
    if (!/;\s*charset\s*=\s*"?utf-?8"?\s*(;|$)/i.test(template.contentType)) {
      errors.push("contentType charset must be UTF-8 (request bodies are sent as UTF-8)");
    }
  }
  if (template.fields !== undefined && !Array.isArray(template.fields)) {
    errors.push("fields must be a list");
  }
  const seen = new Set();
  (Array.isArray(template.fields) ? template.fields : []).forEach((f, i) => {
    const where = `fields[${i}]`;
    if (!isPlainObject(f)) {
      errors.push(`${where} must be an object`);
      return;
    }
    unknownKeys(f, FIELD_KEYS).forEach((k) =>
      errors.push(`${where} has an unknown key: ${k}`)
    );
    if (isBlank(f.buyerField)) {
      errors.push(`${where}.buyerField is required`);
    } else if (seen.has(f.buyerField)) {
      errors.push(`buyer field appears twice: ${f.buyerField}`);
    } else {
      seen.add(f.buyerField);
    }
    const sources = ["ldsField", "value", "secretRef", "envRef"].filter(
      (k) => f[k] !== undefined && f[k] !== null
    );
    if (sources.length !== 1) {
      errors.push(`${where} needs exactly one of ldsField, value, secretRef or envRef`);
    }
    if (typeof f.ldsField === "string" && NOT_MAPPABLE_FIELDS.includes(f.ldsField)) {
      errors.push(`${where}.ldsField ${f.ldsField} is never sent to a buyer`);
    }
    if (f.secretRef !== undefined && f.secretRef !== null) {
      checkSecretRef(f.secretRef, where, errors);
      const extra = Object.keys(f).filter(
        (k) => k !== "buyerField" && k !== "secretRef"
      );
      if (extra.length) {
        errors.push(
          `${where} is a secret field: only buyerField and secretRef are allowed`
        );
      }
    }
    if (f.envRef !== undefined && f.envRef !== null) {
      if (typeof f.envRef !== "string" || !/^LDS_BUYER_VALUE_[A-Z0-9_]{1,64}$/.test(f.envRef)) {
        errors.push(`${where}.envRef must name LDS_BUYER_VALUE_* (A-Z, 0-9, _)`);
      }
      if (isCredentialName(f.buyerField)) errors.push(`${where}.envRef cannot supply a credential; use secretRef`);
      if (Object.keys(f).some((key) => key !== "buyerField" && key !== "envRef")) {
        errors.push(`${where} is an environment field: only buyerField and envRef are allowed`);
      }
    }
    if (isCredentialName(f.buyerField)) {
      // a credential field takes its value from an env var only: no literal source of any kind
      const literals = ["value", "defaultValue", "unmappedValue", "valueMap"].filter(
        (k) => f[k] !== undefined && f[k] !== null
      );
      if (literals.length) {
        errors.push(
          `${where} (${f.buyerField}) looks like a credential: use secretRef with an LDS_BUYER_SECRET_* name, not a typed ${literals.join(
            "/"
          )}`
        );
      }
    }
    if (
      (f.ldsField === undefined || f.ldsField === null) &&
      LDS_ONLY_OPTIONS.some((k) => f[k] !== undefined)
    ) {
      errors.push(
        `${where}: valueMap, unmappedValue, defaultValue and required apply to ldsField only`
      );
    }
    ["required", "omitIfEmpty", "testOnly"].forEach((k) => {
      if (f[k] !== undefined && typeof f[k] !== "boolean") {
        errors.push(`${where}.${k} must be true or false`);
      }
    });
    if (f.valueType !== undefined && !["string", "boolean", "number", "decimal"].includes(f.valueType)) {
      errors.push(`${where}.valueType must be string, boolean, number or decimal`);
    }
    if (f.secretRef !== undefined && f.valueType !== undefined && f.valueType !== "string") {
      errors.push(`${where}: secrets support only string valueType`);
    }
    if (f.transforms !== undefined && !Array.isArray(f.transforms)) {
      errors.push(`${where}.transforms must be a list`);
    }
    if (f.valueMap !== undefined) {
      if (!Array.isArray(f.valueMap) || f.valueMap.length === 0) {
        errors.push(`${where}.valueMap needs at least one row`);
      } else {
        const froms = new Set();
        f.valueMap.forEach((row, r) => {
          if (!isPlainObject(row) || isBlank(row.from) || row.to === undefined) {
            errors.push(`${where}.valueMap[${r}] needs from (not blank) and to`);
            return;
          }
          const key = row.from.trim().toLowerCase();
          if (froms.has(key)) {
            errors.push(
              `${where}.valueMap[${r}].from appears twice (matching ignores case)`
            );
          }
          froms.add(key);
        });
      }
    }
    if (f.unmappedValue !== undefined && f.valueMap === undefined) {
      errors.push(`${where}.unmappedValue needs a valueMap`);
    }
  });
  if (template.auth !== undefined && template.auth !== null) {
    const auth = template.auth;
    if (!isPlainObject(auth)) {
      errors.push("auth must be an object");
    } else {
      const extra = unknownKeys(auth, AUTH_KEYS);
      if (extra.length) {
        errors.push(
          `auth only takes type, headerName, username and secretRef (found: ${extra.join(
            ", "
          )}). ${SECRET_RULE_HINT}`
        );
      }
      const type = String(auth.type || "none").toLowerCase();
      if (!["none", "header", "basic", "bearer"].includes(type)) {
        errors.push("auth.type must be none, header, basic or bearer");
      } else if (type === "none") {
        if (auth.secretRef !== undefined) {
          errors.push(
            "auth.secretRef is only used with auth.type header, basic or bearer"
          );
        }
      } else {
        checkSecretRef(auth.secretRef, "auth", errors);
        if (type === "header" && isBlank(auth.headerName)) {
          errors.push("auth.headerName is required for header auth");
        }
        if (type === "basic" && isBlank(auth.username)) {
          errors.push("auth.username is required for basic auth");
        }
      }
    }
  }
  errors.push(...secretValueErrors(template, "requestTemplate"));
  return errors;
}

function secretValueErrors(root, name) {
  return findSecretValues(root, name).map(
    ({ path, reason }) =>
      `${path} looks like a secret value (${reason}). ${SECRET_RULE_HINT}`
  );
}

export function validateResponseRules(rules) {
  const errors = [];
  if (rules === null) return errors; // null = legacy JSON contract
  if (!isPlainObject(rules)) return ["responseRules must be a JSON object"];
  unknownKeys(rules, RULES_KEYS).forEach((k) =>
    errors.push(`responseRules has an unknown key: ${k}`)
  );
  if (
    rules.format !== undefined &&
    !["json", "xml", "text"].includes(String(rules.format).toLowerCase())
  ) {
    errors.push("format must be json, xml or text");
  }
  if (isBlank(rules.statusPath)) errors.push("statusPath is required");
  if (
    rules.numericStatus !== undefined &&
    typeof rules.numericStatus !== "boolean"
  ) {
    errors.push("numericStatus must be true or false");
  }
  if (!Array.isArray(rules.outcomes) || rules.outcomes.length === 0) {
    errors.push(
      "outcomes is required (status value -> sold, rejected, in_progress, error or not_found)"
    );
  } else {
    const values = new Set();
    rules.outcomes.forEach((o, i) => {
      if (!isPlainObject(o)) {
        errors.push(`outcomes[${i}] must be an object`);
        return;
      }
      unknownKeys(o, OUTCOME_KEYS).forEach((k) =>
        errors.push(`outcomes[${i}] has an unknown key: ${k}`)
      );
      if (isBlank(o.value)) {
        errors.push(`outcomes[${i}].value is required`);
      } else {
        const key = o.value.trim().toLowerCase();
        if (values.has(key)) errors.push(`outcomes[${i}].value appears twice`);
        values.add(key);
      }
      if (!OUTCOMES.includes(o.outcome)) {
        errors.push(
          `outcomes[${i}].outcome must be sold, rejected, in_progress, error or not_found`
        );
      }
    });
    if (!rules.outcomes.some((o) => o && o.outcome === "sold")) {
      errors.push("outcomes needs at least one sold value");
    }
  }
  errors.push(...secretValueErrors(rules, "responseRules"));
  return errors;
}

const isWholeNumber = (v) => typeof v === "number" && Number.isInteger(v);

export function validateTimeouts(timeouts, buyerWindowMs = DEFAULT_BUYER_WINDOW_MS) {
  const errors = [];
  if (!isPlainObject(timeouts)) {
    return ['timeouts must be a JSON object like { "timeout": 10000 }'];
  }
  unknownKeys(timeouts, TIMEOUT_KEYS).forEach((k) =>
    errors.push(`timeouts has an unknown key: ${k}`)
  );
  const { timeout, minTimeoutMs } = timeouts;
  const range = `${TIMEOUT_MIN_MS} and ${TIMEOUT_MAX_MS}`;
  if (
    !isWholeNumber(timeout) ||
    timeout < TIMEOUT_MIN_MS ||
    timeout > TIMEOUT_MAX_MS
  ) {
    errors.push(`timeout is in milliseconds and must be between ${range}`);
  }
  if (minTimeoutMs !== undefined && minTimeoutMs !== null) {
    if (
      !isWholeNumber(minTimeoutMs) ||
      minTimeoutMs < TIMEOUT_MIN_MS ||
      minTimeoutMs > TIMEOUT_MAX_MS
    ) {
      errors.push(
        `minTimeoutMs is a whole number of milliseconds between ${range}, or null`
      );
    } else {
      if (isWholeNumber(timeout) && minTimeoutMs > timeout - SETUP_HEADROOM_MS) {
        errors.push(
          `minTimeoutMs (${minTimeoutMs}) must be at least ${SETUP_HEADROOM_MS} ms below timeout (${timeout})`
        );
      }
      if (minTimeoutMs > buyerWindowMs) {
        errors.push(
          `minTimeoutMs (${minTimeoutMs}) is more than a lead's whole buyer time (${buyerWindowMs} ms): this buyer would never be sent`
        );
      }
    }
  }
  return errors;
}

// Every env var name the template reads (body fields and auth). Names only: values never reach the UI.
export function listSecretRefs(template) {
  if (!isPlainObject(template)) return [];
  const refs = [];
  (Array.isArray(template.fields) ? template.fields : []).forEach((f) => {
    if (isPlainObject(f) && typeof f.secretRef === "string") {
      refs.push({ where: `body field ${f.buyerField}`, name: f.secretRef });
    }
  });
  if (isPlainObject(template.auth) && typeof template.auth.secretRef === "string") {
    refs.push({
      where: `auth (${template.auth.type || "none"})`,
      name: template.auth.secretRef,
    });
  }
  return refs;
}

// Parses and validates the three editors. `errors` is keyed by section; `payload` is set only when all are clean.
export function buildIntegrationPayload(base, texts, buyerWindowMs, { sandbox = false } = {}) {
  const errors = { requestTemplate: [], responseRules: [], timeouts: [], general: [] };
  if (!base || !base.campaignId) {
    errors.general.push("No campaign selected");
  } else if (isBlank(base.postingUrl) || isBlank(base.campaignIntegration)) {
    errors.general.push(
      "Set a posting URL first (live: on the Integration tab, sandbox: in the Sandbox posting URL field here)"
    );
  }

  if (base) errors.general.push(...baseSecretErrors(base));

  const template = parseJsonText(texts.requestTemplate);
  if (template.error) errors.requestTemplate.push(template.error);
  else errors.requestTemplate.push(...validateRequestTemplate(template.value));

  const rules = parseJsonText(texts.responseRules);
  if (rules.error) errors.responseRules.push(rules.error);
  else errors.responseRules.push(...validateResponseRules(rules.value));

  const timeouts = parseJsonText(texts.timeouts);
  if (timeouts.error) errors.timeouts.push(timeouts.error);
  else if (timeouts.empty) errors.timeouts.push("timeout is required");
  else errors.timeouts.push(...validateTimeouts(timeouts.value, buyerWindowMs));

  if (
    !template.error &&
    listSecretRefs(template.value).length > 0 &&
    typeof base?.postingUrl === "string" &&
    !/^https:\/\//i.test(base.postingUrl.trim())
  ) {
    errors.general.push(
      "This template uses LDS_BUYER_SECRET_* values, so the posting URL must be https:// (and its host listed in LDS_BUYER_HOST_<NAME>)."
    );
  }

  const ok = Object.values(errors).every((list) => list.length === 0);
  if (!ok) return { errors, payload: null };

  return {
    errors,
    payload: {
      campaignId: base.campaignId,
      campaignIntegration: base.campaignIntegration,
      postingUrl: base.postingUrl,
      customField: base.customField || [],
      timeout: timeouts.value.timeout,
      // present-but-null clears a stored value; the API keeps a stored value only when the key is absent
      minTimeoutMs:
        timeouts.value.minTimeoutMs === undefined ? null : timeouts.value.minTimeoutMs,
      requestTemplate: template.value,
      responseRules: rules.value,
      // live and sandbox are separate integrations (lds-api #333 stack); always say which one
      sandbox: Boolean(sandbox),
    },
  };
}

// The Integration tab's values are re-sent on save, so they must not carry a secret either.
export function baseSecretErrors(base) {
  const errors = [];
  const hint = "Remove it where it is set (Integration tab or the sandbox posting URL) and use an LDS_BUYER_SECRET_* env var on the API instead.";
  if (typeof base.postingUrl === "string" && secretReason(base.postingUrl)) {
    errors.push(`The posting URL looks like it carries a secret (${secretReason(base.postingUrl)}). ${hint}`);
  }
  (Array.isArray(base.customField) ? base.customField : []).forEach((c, i) => {
    const key = c && typeof c.key === "string" ? c.key : "";
    const value = c && typeof c.value === "string" ? c.value : "";
    if (secretReason(key, { valueLike: true })) {
      errors.push(`Custom field ${i + 1} has a key that looks like a secret value (${secretReason(key, { valueLike: true })}). ${hint}`);
    } else if (isCredentialName(key) && value.trim() !== "") {
      errors.push(`Custom field ${i + 1} looks like a credential (by its name). ${hint}`);
    } else if (secretReason(value, { valueLike: true })) {
      errors.push(`Custom field ${i + 1} looks like a secret value (${secretReason(value, { valueLike: true })}). ${hint}`);
    }
  });
  return errors;
}

// Places an API 400 message under the editor it belongs to.
export function apiErrorSection(message) {
  const text = String(message || "");
  if (/requestTemplate/.test(text)) return "requestTemplate";
  if (/responseRules/.test(text)) return "responseRules";
  if (/minTimeoutMs|timeout/i.test(text)) return "timeouts";
  return "general";
}

// ---- Access (staff login, lds-api #340) ----
// lds-api is the gate: every /buyer/** route, get-campaign-integration included, needs the LDS admin role and
// answers 401/403 otherwise. The role comes from a configurable token claim or the API's env lists, so the
// screen does not second-guess it locally: it loads, and a 401/403 closes the screen.
export function readAccessToken(storage = window.localStorage) {
  try {
    return JSON.parse(storage.getItem("userCredential"))?.access_token || null;
  } catch (e) {
    return null;
  }
}

// RTK Query result -> what the screen shows. Admin endpoints answer HTTP 200 + envelope; staff login
// answers 401/403 at the HTTP level.
export function classifyApiResult(result) {
  const httpStatus = result?.error?.status;
  if (httpStatus === 401 || httpStatus === 403) return { kind: "forbidden" };
  const body = result?.data;
  if (body && !body.error && body?.status?.code === 200) return { kind: "ok", data: body.data };
  if (body?.status?.code === 401 || body?.status?.code === 403) return { kind: "forbidden" };
  const message =
    (typeof body?.data === "string" && body.data) ||
    (typeof result?.error?.data?.error === "string" && result.error.data.error) ||
    null;
  return { kind: "error", message, httpStatus: httpStatus || null };
}

// ---- Send test lead (lds-api task 052: POST /buyer/send-test-lead {campaignId}) ----
// Feature check: POST a body WITHOUT campaignId. Contract asked of 052 (recorded in the PR): such a body is
// answered with the 400 envelope before anything is built or sent. A 404/405/501 means the route does not exist.
export function testLeadProbeState({ httpStatus, body, networkError }) {
  if (networkError) return { enabled: false, reason: "unreachable" };
  if (httpStatus === 401 || httpStatus === 403) return { enabled: false, reason: "forbidden" };
  if (httpStatus === 404 || httpStatus === 405 || httpStatus === 501) {
    return { enabled: false, reason: "missing" };
  }
  const code = body && typeof body === "object" && body.status ? body.status.code : null;
  if (code === 401 || code === 403) return { enabled: false, reason: "forbidden" };
  // only the recorded 052 answer to a body without campaignId proves the endpoint is there and safe
  if (httpStatus === 200 && code === 400 && body.error === true) return { enabled: true, reason: null };
  if (code === 404 || code === 501) return { enabled: false, reason: "missing" };
  if (typeof code === "number") return { enabled: false, reason: "failing" };
  return { enabled: false, reason: "missing" };
}

export const TEST_LEAD_TOOLTIPS = {
  checking: "Checking whether the LDS API can send test leads...",
  missing:
    "Not available yet: this LDS API has no send-test-lead endpoint (task CU-LS-M1-052). Today a test lead can only come in through a publisher channel with testMode=1.",
  forbidden: "Sending a test lead needs the LDS admin role.",
  unreachable: "Could not reach the LDS API to check for the send-test-lead endpoint.",
  failing:
    "The LDS API's send-test-lead endpoint did not answer as expected, so the button stays off. Try again later.",
  ready:
    "Posts one synthetic test lead (testMode=1) to this campaign's sandbox integration through the LDS API. Never sent live.",
};

// Copy of an API answer that is safe to show: any string that looks like a secret is replaced.
export function maskSecretsForDisplay(value) {
  if (typeof value === "string") {
    return findSecretValues(value, "v").length ? "[hidden: looks like a secret]" : value;
  }
  if (Array.isArray(value)) return value.map(maskSecretsForDisplay);
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = maskSecretsForDisplay(value[k]);
    });
    return out;
  }
  return value;
}
